/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';
import fmt from 'indian-number-format';

// Define TypeScript interfaces
interface DailyReportItem {
  loanNumber: string;
  branch: string;
  customerName: string;
  amountCollected: number;
  principalCollected: number;
  onlinePrincipal: number;
  offlinePrincipal: number;
  interestCollected: number;
  onlineInterest: number;
  offlineInterest: number;
  reducingInterest: number;
  principalOutstanding: number;
  interestOutstanding: number;
  penalInterest: number;
  collectionMode: string;
  remarks: string;
}

// Every numeric column gets a total; the order matches the table columns below so the
// summary cells line up with what they are summing.
const amountKeys = [
  'amountCollected',
  'principalCollected',
  'onlinePrincipal',
  'offlinePrincipal',
  'interestCollected',
  'onlineInterest',
  'offlineInterest',
  'reducingInterest',
  'principalOutstanding',
  'interestOutstanding',
  'penalInterest',
] as const;

type AmountKey = (typeof amountKeys)[number];

interface DailyReportProps {
  data: DailyReportItem[];
}

const columns: ColumnsType<DailyReportItem> = [
  {
    title: 'Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    key: 'customerName',
  },
  {
    title: 'Amount Collected',
    dataIndex: 'amountCollected',
    key: 'amountCollected',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Principal Collected',
    dataIndex: 'principalCollected',
    key: 'principalCollected',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Online Principal',
    dataIndex: 'onlinePrincipal',
    key: 'onlinePrincipal',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Offline Principal',
    dataIndex: 'offlinePrincipal',
    key: 'offlinePrincipal',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Interest Collected',
    dataIndex: 'interestCollected',
    key: 'interestCollected',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Online Interest',
    dataIndex: 'onlineInterest',
    key: 'onlineInterest',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Offline Interest',
    dataIndex: 'offlineInterest',
    key: 'offlineInterest',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Reducing Interest',
    dataIndex: 'reducingInterest',
    key: 'reducingInterest',
    render: (amt: number) => fmt.format(amt ?? 0),
  },
  {
    title: 'Principal Outstanding',
    dataIndex: 'principalOutstanding',
    key: 'principalOutstanding',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Interest Outstanding',
    dataIndex: 'interestOutstanding',
    key: 'interestOutstanding',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Penal Interest/Others',
    dataIndex: 'penalInterest',
    key: 'penalInterest',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Collection Mode',
    dataIndex: 'collectionMode',
    key: 'collectionMode',
  },
  {
    title: 'Remarks',
    dataIndex: 'remarks',
    key: 'remarks',
  },
];

const DailyReportDisplay: React.FC<DailyReportProps> = ({ data }) => {
  // Totals cover the whole report, not just the visible page.
  const totals = useMemo(() => {
    const seed = Object.fromEntries(amountKeys.map((key) => [key, 0])) as Record<
      AmountKey,
      number
    >;
    return data.reduce((acc, item) => {
      amountKeys.forEach((key) => {
        acc[key] += Number(item[key] ?? 0);
      });
      return acc;
    }, seed);
  }, [data]);

  return (
    <div className="loan-data-container">
      <h2>Daily Collection Report</h2>
      <Table
        columns={columns}
        dataSource={data}
        rowKey={(_, idx) => (idx !== undefined ? idx.toString() : '')}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="totals-row">
              <Table.Summary.Cell index={0} colSpan={3}>
                Total
              </Table.Summary.Cell>
              {amountKeys.map((key, position) => (
                <Table.Summary.Cell key={key} index={position + 3}>
                  {fmt.format(Number(totals[key].toFixed(2)))}
                </Table.Summary.Cell>
              ))}
              <Table.Summary.Cell index={14}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={15}>-</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default DailyReportDisplay;
